// src/pages/dashboards/DashboardEditPage.tsx
import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Button, Card, Input, Label, Textarea, Spinner, Badge } from '@/components/ui';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/Select';
import { SwitchWithLabel } from '@/components/ui/Switch';
import { ArrowLeft, Save, Plus, Trash2, GripVertical } from 'lucide-react';
import {
  useDashboard,
  useUpdateDashboard,
  useDeleteWidget,
} from '@/hooks/queries/useReportes';
import { PeriodoReporte, TipoWidget } from '@/types/reporte';
import { AddWidgetModal } from './components/AddWidgetModal';
import { WidgetRenderer } from './components/WidgetRenderer';

const PERIODO_LABELS: Record<PeriodoReporte, string> = {
  [PeriodoReporte.HOY]: 'Hoy',
  [PeriodoReporte.AYER]: 'Ayer',
  [PeriodoReporte.SEMANA]: 'Esta semana',
  [PeriodoReporte.SEMANA_PASADA]: 'Semana pasada',
  [PeriodoReporte.MES]: 'Este mes',
  [PeriodoReporte.MES_PASADO]: 'Mes pasado',
  [PeriodoReporte.TRIMESTRE]: 'Este trimestre',
  [PeriodoReporte.ANO]: 'Este ano',
  [PeriodoReporte.PERSONALIZADO]: 'Personalizado',
};

const TIPO_WIDGET_LABELS: Record<TipoWidget, string> = {
  [TipoWidget.CONTADOR]: 'Contador',
  [TipoWidget.GRAFICO_LINEA]: 'Grafico de Linea',
  [TipoWidget.GRAFICO_BARRA]: 'Grafico de Barras',
  [TipoWidget.GRAFICO_PIE]: 'Grafico Pie',
  [TipoWidget.GRAFICO_DONA]: 'Grafico Dona',
  [TipoWidget.TABLA]: 'Tabla',
  [TipoWidget.LISTA]: 'Lista',
  [TipoWidget.PROGRESO]: 'Barra de Progreso',
  [TipoWidget.TENDENCIA]: 'Tendencia',
};

const schema = z.object({
  nombre: z.string().min(1, 'Nombre requerido').max(100, 'Maximo 100 caracteres'),
  descripcion: z.string().max(500, 'Maximo 500 caracteres').optional(),
  periodo_default: z.nativeEnum(PeriodoReporte),
  es_compartido: z.boolean(),
  auto_refresh_segundos: z.coerce.number().min(0).max(3600).optional(),
});

type FormData = z.infer<typeof schema>;

export function DashboardEditPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: dashboard, isLoading } = useDashboard(id!);
  const updateMutation = useUpdateDashboard();
  const deleteWidgetMutation = useDeleteWidget();

  const [isAddWidgetOpen, setIsAddWidgetOpen] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors, isSubmitting, isDirty },
  } = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      nombre: '',
      descripcion: '',
      periodo_default: PeriodoReporte.MES,
      es_compartido: false,
      auto_refresh_segundos: undefined,
    },
  });

  const periodoDefault = watch('periodo_default');
  const esCompartido = watch('es_compartido');

  // Cargar datos del dashboard
  useEffect(() => {
    if (dashboard) {
      reset({
        nombre: dashboard.nombre,
        descripcion: dashboard.descripcion ?? '',
        periodo_default: dashboard.periodo_default,
        es_compartido: dashboard.es_compartido,
        auto_refresh_segundos: dashboard.auto_refresh_segundos ?? undefined,
      });
    }
  }, [dashboard, reset]);

  const onSubmit = async (data: FormData) => {
    try {
      await updateMutation.mutateAsync({
        id: id!,
        data: {
          nombre: data.nombre,
          descripcion: data.descripcion || undefined,
          periodo_default: data.periodo_default,
          es_compartido: data.es_compartido,
          auto_refresh_segundos: data.auto_refresh_segundos || undefined,
        },
      });
      navigate(`/dashboards/${id}`);
    } catch {
      // Error handling is done by react-query
    }
  };

  const handleDeleteWidget = async (widgetId: string) => {
    if (window.confirm('Estas seguro de que deseas eliminar este widget?')) {
      await deleteWidgetMutation.mutateAsync({
        dashboardId: id!,
        widgetId,
      });
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <Spinner size="lg" />
      </div>
    );
  }

  if (!dashboard) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">Dashboard no encontrado</p>
        <Button variant="ghost" onClick={() => navigate('/dashboards')} className="mt-4">
          Volver a dashboards
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate(`/dashboards/${id}`)}
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold">Editar Dashboard</h1>
            <p className="text-gray-500">{dashboard.nombre}</p>
          </div>
        </div>
        <Button onClick={handleSubmit(onSubmit)} disabled={isSubmitting || !isDirty}>
          <Save className="h-4 w-4 mr-2" />
          {isSubmitting ? 'Guardando...' : 'Guardar cambios'}
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Formulario de configuracion */}
        <Card className="p-6 lg:col-span-1">
          <h2 className="text-lg font-semibold mb-4">Configuracion</h2>
          <form className="space-y-4">
            <div>
              <Label htmlFor="nombre">Nombre *</Label>
              <Input
                id="nombre"
                {...register('nombre')}
                placeholder="Mi Dashboard"
                className="mt-1"
              />
              {errors.nombre && (
                <p className="text-sm text-red-500 mt-1">{errors.nombre.message}</p>
              )}
            </div>

            <div>
              <Label htmlFor="descripcion">Descripción</Label>
              <Textarea
                id="descripcion"
                {...register('descripcion')}
                placeholder="Descripción opcional..."
                rows={3}
                className="mt-1"
              />
              {errors.descripcion && (
                <p className="text-sm text-red-500 mt-1">{errors.descripcion.message}</p>
              )}
            </div>

            <div>
              <Label htmlFor="periodo_default">Periodo por defecto</Label>
              <Select
                value={periodoDefault}
                onValueChange={(value) =>
                  setValue('periodo_default', value as PeriodoReporte, { shouldDirty: true })
                }
              >
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(PERIODO_LABELS).map(([value, label]) => (
                    <SelectItem key={value} value={value}>
                      {label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="auto_refresh_segundos">Auto-refresh (segundos)</Label>
              <Input
                id="auto_refresh_segundos"
                type="number"
                {...register('auto_refresh_segundos')}
                placeholder="0 = desactivado"
                min={0}
                max={3600}
                className="mt-1"
              />
              <p className="text-xs text-gray-500 mt-1">
                Deja en 0 o vacio para desactivar
              </p>
            </div>

            <div className="pt-2">
              <SwitchWithLabel
                checked={esCompartido}
                onCheckedChange={(checked) =>
                  setValue('es_compartido', checked, { shouldDirty: true })
                }
                label="Compartir dashboard"
                description="Otros usuarios podran ver este dashboard"
              />
            </div>
          </form>
        </Card>

        {/* Lista de widgets */}
        <Card className="p-6 lg:col-span-2">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold">Widgets ({dashboard.widgets.length})</h2>
            <Button onClick={() => setIsAddWidgetOpen(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Agregar widget
            </Button>
          </div>

          {dashboard.widgets.length === 0 ? (
            <div className="text-center py-8 border-2 border-dashed rounded-lg">
              <p className="text-gray-500 mb-4">No hay widgets en este dashboard</p>
              <Button variant="secondary" onClick={() => setIsAddWidgetOpen(true)}>
                <Plus className="h-4 w-4 mr-2" />
                Agregar primer widget
              </Button>
            </div>
          ) : (
            <div className="space-y-3">
              {dashboard.widgets
                .sort((a, b) => a.orden - b.orden)
                .map((widget) => (
                  <div
                    key={widget.id}
                    className="flex items-center gap-3 p-3 border rounded-lg hover:bg-gray-50"
                  >
                    <GripVertical className="h-5 w-5 text-gray-400 cursor-grab" />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-medium truncate">{widget.titulo}</span>
                        <Badge className="text-xs">
                          {TIPO_WIDGET_LABELS[widget.tipo_widget]}
                        </Badge>
                        {!widget.visible && (
                          <Badge className="text-xs bg-gray-50 text-gray-600 border border-gray-200">
                            Oculto
                          </Badge>
                        )}
                      </div>
                      <span className="text-sm text-gray-500">
                        Metrica: {widget.metrica.nombre}
                      </span>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      onClick={() => handleDeleteWidget(widget.id)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
            </div>
          )}
        </Card>
      </div>

      {/* Vista previa de widgets */}
      {dashboard.widgets.length > 0 && (
        <Card className="p-6">
          <h2 className="text-lg font-semibold mb-4">Vista previa</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {dashboard.widgets
              .filter((w) => w.visible)
              .sort((a, b) => a.orden - b.orden)
              .map((widget) => (
                <WidgetRenderer key={widget.id} widget={widget} />
              ))}
          </div>
        </Card>
      )}

      {/* Dialog agregar widget */}
      <AddWidgetModal
        open={isAddWidgetOpen}
        onClose={() => setIsAddWidgetOpen(false)}
        dashboardId={id!}
      />
    </div>
  );
}
